import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BACKEND_URL } from '../../lib/api';
import LangPill from '../../components/LangPill';
import { verifyAdminPasskey } from '../../lib/webauthn';

const API = BACKEND_URL;
const TOKEN_KEY = 'admin_console_token';

const PALETTE = {
  navy: '#0a2540',
  navyDeep: '#06182c',
  cream: '#faf8f5',
  gold: '#c9a961',
  magenta: '#d6336c',
  slate: '#475569',
  paper: '#ffffff',
  border: '#e5e1d8',
};

export default function LoginManage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // If a token is already present, validate it against the backend
    // before auto-bouncing to /admin. Otherwise an EXPIRED token would
    // ping-pong the user between /admin (401 → redirect here) and
    // /loginmanage (token-present → redirect to /admin) forever.
    // ref bug: 2026-05-20 admin flicker loop after 24h.
    let cancelled = false;
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) return;
    axios
      .get(`${API}/api/admin/settings`, { headers: { Authorization: `Bearer ${t}` } })
      .then(() => {
        if (!cancelled) navigate('/admin', { replace: true });
      })
      .catch((err) => {
        if (cancelled) return;
        // 401 / 403 / bad token → drop it so the user can re-login cleanly.
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem('admin_console_email');
        }
        // Stay on this page silently — the form will render normally.
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/auth`, { email, password });
      if (data.requires_passkey) {
        // 2FA path — password verified, now require a passkey ceremony.
        try {
          const final = await verifyAdminPasskey(data.stepup_token);
          if (!final?.token) throw new Error('Passkey verification returned no token');
          localStorage.setItem(TOKEN_KEY, final.token);
          localStorage.setItem('admin_console_email', final.email);
          navigate('/admin', { replace: true });
        } catch (err) {
          if (err?.name === 'NotAllowedError') {
            setError('Passkey prompt was cancelled. Please try again.');
          } else {
            setError(err?.response?.data?.detail || err?.message || 'Passkey verification failed');
          }
        }
      } else {
        localStorage.setItem(TOKEN_KEY, data.token);
        localStorage.setItem('admin_console_email', data.email);
        navigate('/admin', { replace: true });
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${PALETTE.navyDeep} 0%, ${PALETTE.navy} 100%)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 50 }}>
        <LangPill variant="dark" />
      </div>      <div style={{
        background: PALETTE.paper, padding: '48px 44px', maxWidth: 440, width: '100%',
        boxShadow: '0 30px 60px rgba(0,0,0,0.35)', borderTop: `4px solid ${PALETTE.gold}`,
      }} data-testid="loginmanage-card">
        <div style={{
          fontSize: 11, letterSpacing: '0.24em', textTransform: 'uppercase',
          fontWeight: 700, color: PALETTE.magenta, marginBottom: 12,
        }}>
          Admin console
        </div>
        <h1 style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: 36, fontWeight: 700, color: PALETTE.navy,
          margin: '0 0 8px', letterSpacing: '-0.02em', lineHeight: 1.1,
        }}>
          Sign in.
        </h1>
        <p style={{ fontSize: 14, color: PALETTE.slate, margin: '0 0 28px' }}>
          Keystone analytics, IP logs and integration settings.
        </p>

        <form onSubmit={submit}>
          <label style={labelStyle}>Email</label>
          <input
            data-testid="lm-email"
            type="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@cloudvoro.com" style={inputStyle} required
          />

          <label style={{ ...labelStyle, marginTop: 18 }}>Password</label>
          <input
            data-testid="lm-password"
            type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••••••" style={inputStyle} required
          />

          {error && (
            <div data-testid="lm-error" style={{
              marginTop: 18, padding: '12px 14px', background: '#fff1f0',
              border: `1px solid ${PALETTE.magenta}`, color: PALETTE.magenta,
              fontSize: 13, fontWeight: 600,
            }}>
              {error}
            </div>
          )}

          <button
            data-testid="lm-submit"
            type="submit" disabled={submitting}
            style={{
              marginTop: 24, width: '100%', padding: '14px 16px',
              background: submitting ? PALETTE.slate : PALETTE.navy,
              color: PALETTE.paper, border: 'none', fontWeight: 600,
              fontSize: 14, letterSpacing: '0.04em', textTransform: 'uppercase',
              cursor: submitting ? 'wait' : 'pointer',
            }}
          >
            {submitting ? 'Signing in…' : 'Sign in →'}
          </button>
        </form>

        <p style={{ marginTop: 28, fontSize: 11, color: PALETTE.slate, lineHeight: 1.5 }}>
          Admin access is logged. All sessions expire after 8 hours.
        </p>
      </div>
    </div>
  );
}

const labelStyle = {
  display: 'block', fontSize: 11, letterSpacing: '0.18em',
  textTransform: 'uppercase', fontWeight: 700, color: PALETTE.navy, marginBottom: 8,
};
const inputStyle = {
  width: '100%', padding: '12px 14px', fontSize: 15,
  border: `1px solid ${PALETTE.border}`, outline: 'none',
  fontFamily: 'inherit', background: PALETTE.cream,
  boxSizing: 'border-box',
};
